/**
 * LAP-768 — Compte de résultat personnel mensuel & annuel avec provisions.
 *
 * Monté dans Mon Budget (consolidé, accountId=null) et dans le détail d'un
 * compte (/compte/:id, accountId fourni).
 *
 * - Vue gated sur `data.period` (jamais de crash si l'API renvoie une autre
 *   forme : on n'affiche l'annuel qu'avec des données annuelles).
 * - Dépenses hiérarchisées : Classification → sous-catégorie, chaque
 *   sous-catégorie dépliable pour voir/éditer les opérations liées.
 * - Provisions : marquer une enveloppe et saisir sa cible annuelle.
 */
import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  CircularProgress,
  Button,
  Collapse,
  Tooltip,
  Alert,
  Snackbar,
  LinearProgress,
  IconButton,
} from '@mui/material'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TuneIcon from '@mui/icons-material/Tune'
import GridOnIcon from '@mui/icons-material/GridOn'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import * as XLSX from 'xlsx'
import { makeGetRequest, makePatchRequest, makePostRequest } from 'utils/api'
import urls from 'utils/urls'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

const P = {
  bg: 'linear-gradient(180deg, #0a1024 0%, #0e1a36 100%)',
  card: 'rgba(255,255,255,0.035)',
  cardHover: 'rgba(255,255,255,0.06)',
  border: 'rgba(255,255,255,0.10)',
  borderStrong: 'rgba(126,156,219,0.30)',
  text: '#e8eefc',
  textDim: '#93a4c8',
  accent: '#7e9cdb',
  positive: '#4ade80',
  negative: '#f87171',
  warn: '#fbbf24',
  mono: '"Roboto Mono","SFMono-Regular","Courier New",monospace',
}

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const chf = (n) =>
  `${Number(n || 0).toLocaleString('fr-CH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} CHF`

const selectSx = {
  color: '#fff',
  fontFamily: P.mono,
  minWidth: 110,
  '.MuiOutlinedInput-notchedOutline': { borderColor: P.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: P.accent },
  '.MuiSvgIcon-root': { color: P.textDim },
}
const menuProps = { PaperProps: { sx: { bgcolor: '#0e1a36', color: P.text } } }

function Amount({ value, color, bold, prefix }) {
  return (
    <Typography
      component="span"
      sx={{
        fontFamily: P.mono,
        fontSize: 13,
        fontWeight: bold ? 700 : 500,
        color: color || P.text,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {prefix}
      {chf(value)}
    </Typography>
  )
}

function Card({ title, accent, right, children }) {
  return (
    <Box
      sx={{
        bgcolor: P.card,
        border: `1px solid ${P.border}`,
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        mb: 1.5,
      }}
    >
      {title && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent || P.accent }} />
            <Typography
              sx={{ color: P.text, fontSize: 12.5, letterSpacing: 0.8, fontWeight: 700, textTransform: 'uppercase' }}
            >
              {title}
            </Typography>
          </Stack>
          {right}
        </Stack>
      )}
      {children}
    </Box>
  )
}

/* ───────────────────────── Export Excel / PDF ───────────────────────── */
function statementTotals(data) {
  if (data.totals) return data.totals
  return {
    revenus: (data.revenus && data.revenus.total) || 0,
    depenses_courantes: (data.depenses_courantes && data.depenses_courantes.total) || 0,
    provisions: (data.provisions && data.provisions.total) || 0,
    cashflow_reel: data.cashflow_reel || 0,
    capacite_epargne: data.capacite_epargne || 0,
  }
}

function flattenStatement(data) {
  const rows = []
  normCategories(data.revenus || {}).forEach((c) =>
    c.items.forEach((it) =>
      rows.push(['Revenus', '', c.categorie, it.souscategorie, it.amount]),
    ),
  )
  normDepenses(data.depenses_courantes || {}).forEach((g) =>
    g.categories.forEach((c) =>
      c.items.forEach((it) =>
        rows.push(['Dépenses', g.classification, c.categorie, it.souscategorie, -it.amount]),
      ),
    ),
  )
  ;((data.provisions && data.provisions.items) || []).forEach((p) =>
    rows.push(['Provisions', '', '', p.envelope, -p.monthly]),
  )
  return rows
}

function exportStatementExcel(data) {
  const tt = statementTotals(data)
  const aoa = [
    ['Compte de résultat', data.date || '', '', '', `${data.currency || 'CHF'}`],
    [],
    ['Section', 'Classification', 'Catégorie', 'Sous-catégorie', 'Montant'],
    ...flattenStatement(data),
    [],
    ['Revenus', '', '', '', tt.revenus],
    ['Dépenses courantes', '', '', '', -tt.depenses_courantes],
    ['Provisions', '', '', '', -tt.provisions],
    ['Cash-flow réel', '', '', '', tt.cashflow_reel],
    ["Capacité d'épargne", '', '', '', tt.capacite_epargne],
  ]
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 20 }, { wch: 24 }, { wch: 14 }]
  XLSX.utils.book_append_sheet(wb, ws, 'Compte de résultat')
  if (data.period === 'year' && Array.isArray(data.months)) {
    const m = [['Mois', 'Revenus', 'Dépenses', 'Provisions', 'Cash-flow', 'Épargne']]
    data.months.forEach((x) =>
      m.push([x.month, x.revenus, x.depenses_courantes, x.provisions, x.cashflow_reel, x.capacite_epargne]),
    )
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(m), 'Par mois')
    if (Array.isArray(data.reconciliation) && data.reconciliation.length) {
      const r = [['Enveloppe', 'Provisionné', 'Dépensé', 'Solde', 'Statut']]
      data.reconciliation.forEach((x) => r.push([x.envelope, x.provisionne, x.depense_reelle, x.solde, x.statut]))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(r), 'Réconciliation')
    }
  }
  XLSX.writeFile(wb, `compte-resultat-${data.date || ''}.xlsx`)
}

function exportStatementPDF(data) {
  const tt = statementTotals(data)
  const fmt = (n) => `${Number(n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`
  const rows = flattenStatement(data)
    .map((r) => `<tr><td>${r[0]}</td><td>${r[1] || ''}</td><td>${r[2] || ''}</td><td>${r[3] || ''}</td><td class="num">${fmt(r[4])}</td></tr>`)
    .join('')
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Compte de résultat ${data.date || ''}</title>
  <style>
    body{font-family:Arial,Helvetica,sans-serif;color:#1a2233;margin:28px;}
    h1{font-size:20px;margin:0 0 2px;} .sub{color:#667;margin:0 0 18px;font-size:12px;}
    table{width:100%;border-collapse:collapse;font-size:12px;} th,td{padding:5px 8px;border-bottom:1px solid #e3e8f0;}
    th{text-align:left;color:#556;border-bottom:2px solid #ccd;} td.num,th.num{text-align:right;font-variant-numeric:tabular-nums;}
    .tot{margin-top:18px;width:340px;margin-left:auto;} .tot td{border:none;padding:3px 8px;} .tot .k{color:#556;} .tot .v{text-align:right;font-weight:700;}
    .hl{border-top:2px solid #ccd;}
  </style></head><body>
    <h1>Compte de résultat</h1><p class="sub">${data.date || ''} · ${data.currency || 'CHF'}</p>
    <table><thead><tr><th>Section</th><th>Classification</th><th>Catégorie</th><th>Sous-catégorie</th><th class="num">Montant</th></tr></thead><tbody>${rows}</tbody></table>
    <table class="tot">
      <tr><td class="k">Revenus</td><td class="v">${fmt(tt.revenus)}</td></tr>
      <tr><td class="k">Dépenses courantes</td><td class="v">- ${fmt(tt.depenses_courantes)}</td></tr>
      <tr><td class="k">Provisions</td><td class="v">- ${fmt(tt.provisions)}</td></tr>
      <tr class="hl"><td class="k">Cash-flow réel</td><td class="v">${fmt(tt.cashflow_reel)}</td></tr>
      <tr><td class="k">Capacité d'épargne</td><td class="v">${fmt(tt.capacite_epargne)}</td></tr>
    </table>
    <script>window.onload=function(){window.print();}</script>
  </body></html>`
  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html)
  w.document.close()
}

export default function CompteResultat({ accountId = null }) {
  const { t } = useTranslation('layout')
  const tr = (k, dflt) => t(`finance.compteResultat.${k}`, dflt)

  const now = new Date()
  // Deep-link SmartFeed : la carte budget annuelle ouvre le compte de résultat
  // directement en « Annuel » (?period=year), la mensuelle en « Mensuel »
  // (?period=month). Uniquement en vue consolidée (pas dans le détail d'un compte).
  const [searchParams] = useSearchParams()
  const paramPeriod = (!accountId ? (searchParams.get('period') || '') : '').toLowerCase()
  const initialPeriod = ['year', 'annual', 'annuel'].includes(paramPeriod) ? 'year' : 'month'
  // Deep-link complet : ?year= et ?month= permettent de viser une période précise.
  // Bornés sur la plage du sélecteur (année courante → -6 ans, mois 1-12) ; à
  // défaut on retombe sur la période courante. Ignorés en détail d'un compte.
  const paramYear = !accountId ? parseInt(searchParams.get('year'), 10) : NaN
  const paramMonth = !accountId ? parseInt(searchParams.get('month'), 10) : NaN
  const initialYear =
    Number.isInteger(paramYear) && paramYear >= now.getFullYear() - 6 && paramYear <= now.getFullYear()
      ? paramYear
      : now.getFullYear()
  const initialMonth =
    Number.isInteger(paramMonth) && paramMonth >= 1 && paramMonth <= 12 ? paramMonth : now.getMonth() + 1
  const [period, setPeriod] = useState(initialPeriod)
  const [year, setYear] = useState(initialYear)
  const [month, setMonth] = useState(initialMonth)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showConfig, setShowConfig] = useState(false)
  const [envelopes, setEnvelopes] = useState([])
  const [subcatOptions, setSubcatOptions] = useState([])
  const [snack, setSnack] = useState('')

  const years = []
  for (let y = now.getFullYear(); y >= now.getFullYear() - 6; y--) years.push(y)

  const dateParam = period === 'year' ? `${year}` : `${year}-${String(month).padStart(2, '0')}`

  // `silent` = rafraîchissement en arrière-plan (après une édition inline) :
  // on ne déclenche PAS le spinner plein écran, sinon la vue se reconstruit et
  // les lignes dépliées se replient. On met aussi à jour `data` sans la vider.
  const fetchStatement = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError('')
    let url = `${urls.financeStatement}?period=${period}&date=${dateParam}`
    if (accountId) url += `&account=${accountId}`
    const { data: res, status } = await makeGetRequest(url, {}, true)
    if (status === 200 && res && res.period) {
      setData(res)
    } else if (!silent) {
      setData(null)
      setError(
        (res && (res.detail || res.error)) ||
          tr('loadError', "Impossible de charger le compte de résultat. Réessayez."),
      )
    }
    if (!silent) setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, dateParam, accountId])

  useEffect(() => {
    fetchStatement()
  }, [fetchStatement])

  const fetchSubcatOptions = async () => {
    const { data: res, status } = await makeGetRequest(
      `${urls.fetchCategories}?limit=100000`,
      {},
      true,
    )
    if (status === 200 && Array.isArray(res)) {
      // Une entrée par (sous-catégorie, mouvement) : un même nom peut exister
      // côté Dépense ET Revenu (ex. « Remboursement »). On garde le mouvement
      // pour filtrer les menus selon le sens de l'opération.
      const seen = new Set()
      const list = []
      res.forEach((c) => {
        const name = (c.SousCategorie || '').trim()
        const mouvement = c.Mouvement || ''
        const key = `${name}::${mouvement}`
        if (name && !seen.has(key)) {
          seen.add(key)
          list.push({ name, categorie: c.Categorie || '', mouvement })
        }
      })
      list.sort((a, b) => a.name.localeCompare(b.name))
      setSubcatOptions(list)
    }
  }

  const fetchEnvelopes = async () => {
    const { data: res, status } = await makeGetRequest(
      `${urls.crudBudgetSousCategoriesOpertations}?limit=100000`,
      {},
      true,
    )
    if (status === 200) {
      const list = res?.results || res || []
      setEnvelopes(Array.isArray(list) ? list : [])
    }
  }

  useEffect(() => {
    fetchSubcatOptions()
  }, [])

  const openConfig = () => {
    if (!showConfig) fetchEnvelopes()
    setShowConfig(!showConfig)
  }

  const patchEnvelope = async (env, payload) => {
    const url = `${urls.crudBudgetSousCategoriesOpertations}${env.id}/`
    const { status } = await makePatchRequest(url, payload, {}, true)
    if (status === 200 || status === 202) {
      setEnvelopes((prev) => prev.map((e) => (e.id === env.id ? { ...e, ...payload } : e)))
      setSnack(tr('saved', 'Enregistré'))
      fetchStatement()
    } else {
      setSnack(tr('saveError', "Échec de l'enregistrement"))
    }
  }

  // Édition inline d'une catégorie d'opération (réutilise auto-categorize/apply).
  const changeOpCategory = async (opId, souscategorie, onDone) => {
    const { status } = await makePostRequest(
      urls.financeAutoCategorizeApply,
      { assignments: [{ op_id: opId, souscategorie }], create_rules: false },
      {},
      true,
    )
    if (status === 200) {
      setSnack(tr('saved', 'Enregistré'))
      if (onDone) onDone()
      fetchStatement(true) // refresh silencieux : ne replie pas les lignes
    } else {
      setSnack(tr('saveError', "Échec de l'enregistrement"))
    }
  }

  const drill = {
    dateParam,
    period,
    accountId,
    subcatOptions,
    onChangeOpCategory: changeOpCategory,
    tr,
  }

  return (
    <Box sx={{ background: P.bg, borderRadius: 2, p: { xs: 1.5, sm: 2.5 }, color: P.text }}>
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* En-tête */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ color: P.text, fontWeight: 700, fontSize: 17 }}>
            {tr('title', 'Compte de résultat')}
            {accountId ? ` · ${tr('perAccount', 'ce compte')}` : ''}
          </Typography>
          <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>
            {tr('subtitle', 'Revenus, dépenses courantes, provisions et capacité d’épargne.')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={period}
            onChange={(e, v) => v && setPeriod(v)}
            sx={{
              '& .MuiToggleButton-root': {
                color: P.textDim, borderColor: P.border, textTransform: 'none', px: 1.5,
              },
              '& .Mui-selected': { color: '#fff !important', bgcolor: 'rgba(126,156,219,0.35) !important' },
            }}
          >
            <ToggleButton value="month">{tr('monthly', 'Mensuel')}</ToggleButton>
            <ToggleButton value="year">{tr('annual', 'Annuel')}</ToggleButton>
          </ToggleButtonGroup>

          {period === 'month' && (
            <Select size="small" value={month} onChange={(e) => setMonth(e.target.value)} sx={selectSx} MenuProps={menuProps}>
              {MONTHS_FR.map((m, i) => (
                <MenuItem key={m} value={i + 1}>{m}</MenuItem>
              ))}
            </Select>
          )}
          <Select size="small" value={year} onChange={(e) => setYear(e.target.value)} sx={selectSx} MenuProps={menuProps}>
            {years.map((y) => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
          </Select>

          {!accountId && (
            <Tooltip title={tr('configProvisions', 'Configurer les provisions')}>
              <Button
                size="small"
                onClick={openConfig}
                startIcon={<TuneIcon />}
                variant="outlined"
                sx={{ color: showConfig ? '#fff' : P.textDim, borderColor: P.border, textTransform: 'none' }}
              >
                {tr('provisions', 'Provisions')}
              </Button>
            </Tooltip>
          )}

          <Tooltip title={tr('exportExcel', 'Export Excel')}>
            <span>
              <IconButton size="small" disabled={!data} onClick={() => data && exportStatementExcel(data)} sx={{ color: P.textDim }}>
                <GridOnIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={tr('exportPdf', 'Export PDF')}>
            <span>
              <IconButton size="small" disabled={!data} onClick={() => data && exportStatementPDF(data)} sx={{ color: P.textDim }}>
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {!accountId && (
        <Collapse in={showConfig} unmountOnExit>
          <ProvisionsConfig envelopes={envelopes} onPatch={patchEnvelope} tr={tr} />
        </Collapse>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} sx={{ color: P.accent }} />
        </Box>
      ) : error ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchStatement}>{tr('retry', 'Réessayer')}</Button>}>
          {error}
        </Alert>
      ) : !data ? null : data.period === 'year' ? (
        <AnnualView data={data} tr={tr} drill={drill} />
      ) : (
        <MonthlyView data={data} tr={tr} drill={drill} />
      )}
    </Box>
  )
}

/* ───────────── Ligne sous-catégorie dépliable (drill-down) ───────────── */
function SubcatRow({ item, color, sign, maxAmount, dateParam, period, accountId, subcatOptions, onChangeOpCategory, tr, optype, mouvement }) {
  const [open, setOpen] = useState(false)
  const [ops, setOps] = useState(null)
  const [loading, setLoading] = useState(false)

  // Menu d'édition restreint aux sous-catégories du bon sens (Dépense/Revenu).
  const editOptions = React.useMemo(() => {
    const filtered = (subcatOptions || []).filter(
      (o) => !mouvement || !o.mouvement || o.mouvement === mouvement,
    )
    const seen = new Set()
    return filtered.filter((o) => (seen.has(o.name) ? false : seen.add(o.name)))
  }, [subcatOptions, mouvement])

  const loadOps = async () => {
    setLoading(true)
    let url = `${urls.financeStatementOperations}?period=${period}&date=${dateParam}&souscategorie=${encodeURIComponent(item.souscategorie)}`
    if (optype) url += `&type=${optype}`
    if (accountId) url += `&account=${accountId}`
    const { data: res, status } = await makeGetRequest(url, {}, true)
    if (status === 200) setOps(res.operations || [])
    setLoading(false)
  }

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && ops === null) loadOps()
  }

  const pct = maxAmount > 0 ? Math.max(2, Math.round((item.amount / maxAmount) * 100)) : 0

  return (
    <Box>
      <Box
        onClick={toggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, py: 0.6, px: 0.5, cursor: 'pointer',
          borderRadius: 1, '&:hover': { bgcolor: P.cardHover },
        }}
      >
        {open ? (
          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: P.textDim }} />
        ) : (
          <KeyboardArrowRightIcon sx={{ fontSize: 18, color: P.textDim }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: P.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.souscategorie}
            <Typography component="span" sx={{ color: P.textDim, fontSize: 11, ml: 1 }}>
              {item.categorie}{item.count ? ` · ${item.count}` : ''}
            </Typography>
          </Typography>
          <Box sx={{ mt: 0.4, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 2, bgcolor: color, opacity: 0.55 }} />
          </Box>
        </Box>
        <Amount value={item.amount} color={color} sign={sign} prefix={sign} />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 3.5, pr: 0.5, pb: 1 }}>
          {loading ? (
            <LinearProgress sx={{ my: 1 }} />
          ) : ops && ops.length === 0 ? (
            <Typography sx={{ color: P.textDim, fontSize: 12, py: 1 }}>
              {tr('noOps', 'Aucune opération.')}
            </Typography>
          ) : (
            (ops || []).map((op) => (
              <Stack
                key={op.op_id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 0.5, borderTop: `1px solid ${P.border}` }}
              >
                <Typography sx={{ color: P.textDim, fontSize: 11.5, fontFamily: P.mono, width: 84, flexShrink: 0 }}>
                  {op.date}
                </Typography>
                <Typography sx={{ flex: 1, fontSize: 12.5, color: P.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={op.libelle}>
                  {op.libelle}
                </Typography>
                <Select
                  size="small"
                  value={editOptions.some((o) => o.name === item.souscategorie) ? item.souscategorie : ''}
                  onChange={(e) => onChangeOpCategory(op.op_id, e.target.value, loadOps)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ ...selectSx, minWidth: 150, fontSize: 12 }}
                  MenuProps={menuProps}
                >
                  {editOptions.map((o) => (
                    <MenuItem key={o.name} value={o.name} sx={{ fontSize: 12.5 }}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
                <Amount value={op.montant} color={op.montant < 0 ? P.negative : P.positive} />
              </Stack>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

/* ─────────── Normalisation hiérarchie (rétro-compatible) ─────────── */
function normItem(it) {
  if (!it) return { souscategorie: '—', categorie: '', amount: 0, count: 0 }
  return {
    souscategorie: it.souscategorie || it.label || it.classification || '—',
    categorie: it.categorie || '',
    amount: Number(it.amount) || 0,
    count: it.count || 0,
  }
}

function normCategories(node) {
  // node attendu : {categories:[{categorie,total,items}]} | {items:[...]} | [...]
  if (node && Array.isArray(node.categories)) {
    return node.categories.map((c) => ({
      categorie: c.categorie || '',
      total: Number(c.total) || 0,
      items: (Array.isArray(c.items) ? c.items : []).map(normItem),
    }))
  }
  const items = (Array.isArray(node && node.items) ? node.items : Array.isArray(node) ? node : []).map(normItem)
  return items.length ? [{ categorie: '', total: items.reduce((s, i) => s + i.amount, 0), items }] : []
}

function normDepenses(dc) {
  if (dc && Array.isArray(dc.groups)) {
    return dc.groups.map((g) => ({
      classification: g.classification || '',
      total: Number(g.total) || 0,
      categories: normCategories(g),
    }))
  }
  const cats = normCategories(dc)
  return cats.length ? [{ classification: '', total: cats.reduce((s, c) => s + c.total, 0), categories: cats }] : []
}

function allItems(groupsOrCats) {
  const out = []
  groupsOrCats.forEach((g) => {
    const cats = g.categories || [g]
    cats.forEach((c) => (c.items || []).forEach((i) => out.push(i)))
  })
  return out
}

/* ─────────── Bloc Catégorie repliable (sous-catégories) ─────────── */
function CategoryBlock({ categorie, total, items, color, sign, optype, mouvement, maxAmount, drill, refTotal }) {
  const [open, setOpen] = useState(true)
  const pct = refTotal > 0 ? Math.round((total / refTotal) * 100) : null
  return (
    <Box sx={{ mb: 0.5 }}>
      {categorie ? (
        <Box
          onClick={() => setOpen(!open)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: P.cardHover } }}
        >
          {open ? <KeyboardArrowDownIcon sx={{ fontSize: 16, color: P.textDim }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 16, color: P.textDim }} />}
          <Typography sx={{ color: P.text, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
            {categorie}
          </Typography>
          {pct != null && (
            <Typography sx={{ color: P.textDim, fontSize: 11, fontFamily: P.mono }}>
              {pct}%
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Amount value={total} color={P.textDim} prefix={sign} />
        </Box>
      ) : null}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: categorie ? 1.5 : 0 }}>
          {items.map((it) => (
            <SubcatRow key={it.souscategorie} item={it} color={color} sign={sign} maxAmount={maxAmount} optype={optype} mouvement={mouvement} {...drill} />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

/* ─────────── Découpage revenus + dépenses (mensuel & annuel) ─────────── */
function Breakdown({ data, tr, drill }) {
  const revCategories = normCategories(data.revenus || {})
  const depGroups = normDepenses(data.depenses_courantes || {})
  const revMax = Math.max(1, ...allItems(revCategories).map((i) => i.amount), 1)
  const depMax = Math.max(1, ...allItems(depGroups).map((i) => i.amount), 1)
  // Référence pour le « % du revenu » : total des revenus de la période.
  const refTotal =
    Number((data.revenus && data.revenus.total) || 0) ||
    revCategories.reduce((s, c) => s + c.total, 0)

  return (
    <>
      <Card title={tr('revenus', 'Revenus')} accent={P.positive} right={<PctLegend tr={tr} />}>
        {revCategories.length === 0 && <Empty tr={tr} />}
        {revCategories.map((c) => (
          <CategoryBlock key={c.categorie} categorie={c.categorie} total={c.total} items={c.items} color={P.positive} sign="+ " optype="R" mouvement="Revenu" maxAmount={revMax} drill={drill} refTotal={refTotal} />
        ))}
      </Card>

      <Card title={tr('depensesCourantes', 'Dépenses courantes')} accent={P.negative} right={<PctLegend tr={tr} />}>
        {depGroups.length === 0 && <Empty tr={tr} />}
        {depGroups.map((g) => (
          <Box key={g.classification || 'sans-classification'} sx={{ mb: 1 }}>
            {g.classification ? (
              <Stack direction="row" alignItems="center" sx={{ gap: 1, px: 0.5, py: 0.5, borderBottom: `1px solid ${P.border}` }}>
                <Typography sx={{ color: P.accent, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  {g.classification}
                </Typography>
                {refTotal > 0 && (
                  <Typography sx={{ color: P.accent, fontSize: 11, fontFamily: P.mono, opacity: 0.85 }}>
                    {Math.round((g.total / refTotal) * 100)}%
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Amount value={g.total} color={P.textDim} prefix="− " />
              </Stack>
            ) : null}
            {g.categories.map((c) => (
              <CategoryBlock key={`${g.classification}::${c.categorie}`} categorie={c.categorie} total={c.total} items={c.items} color={P.negative} sign="− " optype="D" mouvement="Dépense" maxAmount={depMax} drill={drill} refTotal={refTotal} />
            ))}
          </Box>
        ))}
      </Card>
    </>
  )
}

function PctLegend({ tr }) {
  return (
    <Typography sx={{ color: P.textDim, fontSize: 10.5, fontStyle: 'italic' }}>
      {tr('pctOfIncome', '% du revenu')}
    </Typography>
  )
}

/* ───────────────────────── Vue mensuelle ───────────────────────── */
function MonthlyView({ data, tr, drill }) {
  const cf = data.cashflow_reel
  const ep = data.capacite_epargne
  const rev = data.revenus || {}
  const dc = data.depenses_courantes || {}
  const prov = data.provisions || {}
  const provItems = Array.isArray(prov.items) ? prov.items : []

  return (
    <Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Kpi label={tr('totalRevenus', 'Revenus')} value={rev.total} color={P.positive} />
        <Kpi label={tr('totalDepenses', 'Dépenses courantes')} value={dc.total} color={P.negative} />
        <Kpi label={tr('totalProvisions', 'Provisions')} value={prov.total} color={P.warn} />
        <Kpi label={tr('cashflowReel', 'Cash-flow réel')} value={cf} color={(cf || 0) >= 0 ? P.positive : P.negative} />
        <Kpi label={tr('capaciteEpargne', 'Capacité d’épargne')} value={ep} color={(ep || 0) >= 0 ? P.positive : P.negative} highlight />
      </Stack>

      <Breakdown data={data} tr={tr} drill={drill} />

      <Card title={tr('provisionsTitle', 'Provisions (lissage charges non-mensuelles)')} accent={P.warn}>
        {provItems.length === 0 ? (
          <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>
            {tr('noProvision', 'Aucune enveloppe provisionnée. Bouton « Provisions » pour en configurer.')}
          </Typography>
        ) : (
          provItems.map((it, idx) => (
            <Stack key={`${it.envelope}-${idx}`} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Typography sx={{ fontSize: 13, color: P.text }}>
                {it.envelope}
                <Typography component="span" sx={{ color: P.textDim, fontSize: 11, ml: 1 }}>
                  {chf(it.annual_target)}/an
                </Typography>
              </Typography>
              <Amount value={it.monthly} color={P.warn} prefix="− " />
            </Stack>
          ))
        )}
      </Card>
    </Box>
  )
}

function Empty({ tr }) {
  return <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>{tr('empty', 'Aucune donnée sur la période.')}</Typography>
}

/* ───────────────────────── Vue annuelle ───────────────────────── */
function AnnualView({ data, tr, drill }) {
  const tt = data.totals || {}
  const months = data.months || []
  const reconciliation = data.reconciliation || []
  return (
    <Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Kpi label={tr('totalRevenus', 'Revenus')} value={tt.revenus} color={P.positive} />
        <Kpi label={tr('totalDepenses', 'Dépenses courantes')} value={tt.depenses_courantes} color={P.negative} />
        <Kpi label={tr('totalProvisions', 'Provisions')} value={tt.provisions} color={P.warn} />
        <Kpi label={tr('cashflowReel', 'Cash-flow réel')} value={tt.cashflow_reel} color={(tt.cashflow_reel || 0) >= 0 ? P.positive : P.negative} />
        <Kpi label={tr('capaciteEpargne', 'Capacité d’épargne')} value={tt.capacite_epargne} color={(tt.capacite_epargne || 0) >= 0 ? P.positive : P.negative} highlight />
      </Stack>

      {/* Découpage annuel Classification → Catégorie → Sous-catégorie → opérations */}
      <Breakdown data={data} tr={tr} drill={drill} />

      <Card title={tr('monthlyBreakdown', 'Détail par mois')} accent={P.accent}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={tableSx}>
            <thead>
              <tr>
                <th>{tr('month', 'Mois')}</th>
                <th>{tr('revenus', 'Revenus')}</th>
                <th>{tr('depensesShort', 'Dépenses')}</th>
                <th>{tr('provisions', 'Provisions')}</th>
                <th>{tr('cashflowShort', 'Cash-flow')}</th>
                <th>{tr('epargneShort', 'Épargne')}</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month}>
                  <td>{MONTHS_FR[m.month - 1]}</td>
                  <td style={{ color: P.positive }}>{chf(m.revenus)}</td>
                  <td style={{ color: P.negative }}>{chf(m.depenses_courantes)}</td>
                  <td style={{ color: P.warn }}>{chf(m.provisions)}</td>
                  <td style={{ color: m.cashflow_reel >= 0 ? P.positive : P.negative }}>{chf(m.cashflow_reel)}</td>
                  <td style={{ color: m.capacite_epargne >= 0 ? P.positive : P.negative }}>{chf(m.capacite_epargne)}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Card>

      <Card title={tr('reconciliation', 'Réconciliation des provisions')} accent={P.warn}>
        {reconciliation.length === 0 ? (
          <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>{tr('noProvision', 'Aucune enveloppe provisionnée.')}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={tableSx}>
              <thead>
                <tr>
                  <th>{tr('envelope', 'Enveloppe')}</th>
                  <th>{tr('provisioned', 'Provisionné')}</th>
                  <th>{tr('spent', 'Dépensé')}</th>
                  <th>{tr('balance', 'Solde')}</th>
                  <th>{tr('status', 'Statut')}</th>
                </tr>
              </thead>
              <tbody>
                {reconciliation.map((r) => (
                  <tr key={r.envelope}>
                    <td>{r.envelope}</td>
                    <td style={{ color: P.warn }}>{chf(r.provisionne)}</td>
                    <td style={{ color: P.negative }}>{chf(r.depense_reelle)}</td>
                    <td style={{ color: r.solde >= 0 ? P.positive : P.negative }}>{chf(r.solde)}</td>
                    <td style={{ color: r.solde >= 0 ? P.positive : P.negative }}>{r.statut}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  )
}

function Kpi({ label, value, color, highlight }) {
  return (
    <Box
      sx={{
        flex: '1 1 150px', minWidth: 138,
        bgcolor: highlight ? 'rgba(126,156,219,0.12)' : P.card,
        border: `1px solid ${highlight ? P.borderStrong : P.border}`,
        borderRadius: 2, p: 1.5,
      }}
    >
      <Typography sx={{ color: P.textDim, fontSize: 11, letterSpacing: 0.3 }}>{label}</Typography>
      <Typography sx={{ color, fontSize: 18, fontWeight: 700, fontFamily: P.mono, mt: 0.4, fontVariantNumeric: 'tabular-nums' }}>
        {chf(value)}
      </Typography>
    </Box>
  )
}

const tableSx = {
  width: '100%', borderCollapse: 'collapse', fontFamily: P.mono, fontSize: 12.5,
  '& th': { textAlign: 'right', color: P.textDim, fontWeight: 600, padding: '6px 10px', borderBottom: `1px solid ${P.border}`, whiteSpace: 'nowrap' },
  '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
  '& td': { textAlign: 'right', color: P.text, padding: '5px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
}

/* ───────────────── Configuration des provisions ───────────────── */
function ProvisionsConfig({ envelopes, onPatch, tr }) {
  const [drafts, setDrafts] = useState({})
  const setDraft = (id, val) => setDrafts((d) => ({ ...d, [id]: val }))

  const commit = (env) => {
    const raw = drafts[env.id]
    if (raw === undefined) return
    const v = parseFloat(raw)
    if (Number.isNaN(v)) return
    const payload = { annual_target: v }
    // Saisir un montant > 0 marque automatiquement l'enveloppe provisionnable.
    if (v > 0 && !env.is_provisionable) payload.is_provisionable = true
    if (v !== Number(env.annual_target) || payload.is_provisionable) onPatch(env, payload)
  }

  return (
    <Box sx={{ bgcolor: P.card, border: `1px solid ${P.border}`, borderRadius: 2, p: 2, mb: 2 }}>
      <Typography sx={{ color: P.text, fontSize: 12.5, fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {tr('configTitle', 'Enveloppes provisionnables')}
      </Typography>
      <Typography sx={{ color: P.textDim, fontSize: 11.5, mb: 1.5 }}>
        {tr('configHelp', 'Saisissez le montant annuel d’une charge (impôts, assurances…). La provision mensuelle = montant / 12.')}
      </Typography>

      {envelopes.length === 0 && (
        <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>
          {tr('loadingEnvelopes', 'Chargement des enveloppes…')}
        </Typography>
      )}

      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {envelopes.map((env) => {
          const draftVal = drafts[env.id] ?? env.annual_target ?? 0
          return (
            <Stack key={env.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5, borderBottom: `1px solid ${P.border}` }}>
              <Checkbox
                size="small"
                checked={!!env.is_provisionable}
                onChange={(e) => onPatch(env, { is_provisionable: e.target.checked })}
                sx={{ color: P.textDim, '&.Mui-checked': { color: P.warn }, p: 0.5 }}
              />
              <Typography sx={{ flex: 1, color: P.text, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {env.SousCategorie}
                <Typography component="span" sx={{ color: P.textDim, fontSize: 11, ml: 1 }}>{env.Categorie}</Typography>
              </Typography>
              <TextField
                size="small"
                type="number"
                value={draftVal}
                onChange={(e) => setDraft(env.id, e.target.value)}
                onBlur={() => commit(env)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
                InputProps={{ sx: { color: '#fff', fontFamily: P.mono, fontSize: 12.5, width: 130 } }}
                sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: P.border } }}
                label={tr('annualTarget', 'Cible / an')}
                InputLabelProps={{ sx: { color: P.textDim, fontSize: 12 } }}
              />
              <Typography sx={{ color: P.warn, fontFamily: P.mono, fontSize: 12, minWidth: 104, textAlign: 'right' }}>
                {chf((Number(draftVal) || 0) / 12)}/mois
              </Typography>
            </Stack>
          )
        })}
      </Box>
    </Box>
  )
}
